/**
 * ProductionWarning Component
 *
 * Modal dialog that appears when user attempts to switch to Production environment.
 * Requires explicit confirmation before allowing the switch.
 */
import React, { useState } from 'react';
import { useEnvironment } from '../../../hooks/useEnvironment';
import styles from './ProductionWarning.module.css';

export const ProductionWarning: React.FC = () => {
  const { showProductionWarning, confirmProductionSwitch, cancelProductionSwitch } = useEnvironment();
  const [confirmed, setConfirmed] = useState(false);

  if (!showProductionWarning) {
    return null;
  }

  const handleConfirm = () => {
    if (confirmed) {
      confirmProductionSwitch();
      setConfirmed(false); // Reset for next time
    }
  };

  const handleCancel = () => {
    cancelProductionSwitch();
    setConfirmed(false); // Reset checkbox
  };

  return (
    <div className={styles['production-warning-overlay']} role="dialog" aria-modal="true" aria-labelledby="production-warning-title">
      <div className={styles['production-warning-dialog']}>
        <div className={styles['production-warning-icon']}>⚠️</div>

        <h2 id="production-warning-title" className={styles['production-warning-title']}>
          Switch to Production Mode?
        </h2>

        <div className={styles['production-warning-content']}>
          <p className={styles['production-warning-message']}>
            You are about to switch to <strong>Production mode</strong>. Any changes you make will affect the live production database.
          </p>

          <ul className={styles['production-warning-list']}>
            <li>All operations will affect real production data</li>
            <li>Changes cannot be easily undone</li>
            <li>Extra confirmation dialogs will appear for saves and deletes</li>
            <li>Proceed with caution</li>
          </ul>

          <div className={styles['production-warning-checkbox']}>
            <label>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                aria-describedby="confirmation-label"
              />
              <span id="confirmation-label">
                I understand I am switching to Production mode and will proceed with caution
              </span>
            </label>
          </div>
        </div>

        <div className={styles['production-warning-actions']}>
          <button
            className={`${styles.button} ${styles['button-cancel']}`}
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`${styles.button} ${styles['button-confirm']}`}
            onClick={handleConfirm}
            disabled={!confirmed}
            type="button"
          >
            Switch to Production
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductionWarning;
